/**
 * Public event list for a chapter or branch.
 *
 * Route: /c/:chapterSlug/events
 *
 * Reuses ScopeContext to discover the current chapter. Branch-scoped event
 * lists ship with the branch routes in spec 100.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useScope } from '@/contexts/ScopeContext';
import { eventsApi, type EventV2 } from '@/services/api';

export function EventListPage() {
  const { chapter, branch, loading: scopeLoading } = useScope();
  const [events, setEvents] = useState<EventV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (scopeLoading || !chapter) return;
    setLoading(true);
    const target = branch ? { scopeType: 'branch' as const, scopeId: branch.id } : { scopeType: 'chapter' as const, scopeId: chapter.id };
    eventsApi
      .list(target.scopeType, target.scopeId)
      .then((rows) => setEvents(rows))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [scopeLoading, chapter?.id, branch?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (scopeLoading || !chapter) {
    return <div className="container mx-auto px-4 py-12 text-white/60">Loading…</div>;
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <header className="mb-8">
        <h1 className="text-4xl font-display font-bold text-white">Events</h1>
        <p className="text-white/60 mt-1">
          {branch?.displayName ?? branch?.name ?? chapter.displayName}
        </p>
      </header>

      {error && <p className="text-red-400">{error}</p>}
      {loading ? (
        <p className="text-white/60">Loading events…</p>
      ) : events.length === 0 ? (
        <p className="text-white/60">No events scheduled.</p>
      ) : (
        <ul className="space-y-4">
          {events.map((e) => (
            <li key={e.id} className="card flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-white">{e.title}</h2>
                <p className="text-sm text-white/60">
                  {new Date(e.startsAt).toLocaleString()}
                  {e.locationText ? ` · ${e.locationText}` : ''}
                </p>
              </div>
              <Link
                to={`./${e.slug}`}
                className="btn-primary text-sm px-4 py-2"
              >
                Details
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default EventListPage;
